import type { Observation } from "@medplum/fhirtypes";

import { dedupeFinalLaborStageObservations } from "@/encounters/components/EncounterLabor/utils/labor-stage-dedupe";
import type { AppDispatch } from "@/store";
import { loadLaborStages } from "@/store/slices/labor-stages";

export const LABOR_AMEND_PREVIEW_STAGE_ID_PREFIX = "labor-amend-preview-";

/** True for synthetic labor-stage observations added only for amend move previews. */
export function isLaborAmendPreviewStageObservation(observation: Observation | undefined): boolean {
  return Boolean(observation?.id?.startsWith(LABOR_AMEND_PREVIEW_STAGE_ID_PREFIX));
}

/**
 * Dedupes server labor stages while keeping synthetic amend preview placeholders in Redux.
 * Used when live observation resync replaces labor stages during defer-amend editing.
 */
export function dedupeLaborStagesPreservingAmendPreviews(
  serverStages: Observation[],
  laborStageEntities: Record<string, Observation | undefined>,
): Observation[] {
  const previewStages = Object.values(laborStageEntities).filter(
    (observation): observation is Observation => isLaborAmendPreviewStageObservation(observation),
  );
  if (previewStages.length === 0) {
    return dedupeFinalLaborStageObservations(serverStages);
  }
  return dedupeFinalLaborStageObservations([...serverStages, ...previewStages]);
}

/** Removes preview-only labor stages from Redux after cancel or successful Sign & Lock. */
export function stripLaborAmendPreviewStages(
  dispatch: AppDispatch,
  laborStageEntities: Record<string, Observation | undefined>,
): void {
  const entities = Object.values(laborStageEntities).filter(
    (observation): observation is Observation =>
      Boolean(observation) && !isLaborAmendPreviewStageObservation(observation),
  );
  dispatch(loadLaborStages(entities));
}
